import { useEffect, useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, X, FileText, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { VA_BLACK, NAVY } from '../../styles/tokens';
import { STATUS_COLORS, STATUS_LABEL } from '../../../../shared/constants';
import AuthedReceiptPreview from '../../shared/components/AuthedReceiptPreview';

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span style={{
      background: s.bg, color: s.text, border: `1px solid ${s.border}`,
      borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: '600',
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function fmt(amount) {
  if (amount === null || amount === undefined) return '—';
  return '$' + parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function ReceiptViewerModal({ receipt, onClose, onPrev, onNext, hasPrev, hasNext }) {
  // Keyboard navigation
  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape')       onClose();
      if (e.key === 'ArrowLeft'  && hasPrev) onPrev();
      if (e.key === 'ArrowRight' && hasNext) onNext();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  // V32 (Enh 2): zoom + pan state for the image viewer. Reset whenever the receipt changes
  // (navigation) so a new image always opens fit-to-screen at 100%.
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);

  useEffect(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, [receipt?.previewUrl]);

  if (!receipt) return null;

  const isImage = receipt.fileType?.startsWith('image/');
  const isPdf   = receipt.fileType === 'application/pdf';

  const MIN_SCALE = 1, MAX_SCALE = 5, STEP = 0.5;
  const atDefault = scale === 1 && offset.x === 0 && offset.y === 0;
  const clampScale = (s) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));
  function zoomBy(delta) {
    setScale(prev => {
      const next = clampScale(prev + delta);
      if (next === MIN_SCALE) setOffset({ x: 0, y: 0 }); // snap back to center at 100%
      return next;
    });
  }
  function resetZoom() { setScale(1); setOffset({ x: 0, y: 0 }); }
  function handleWheel(e) {
    if (!isImage) return;
    e.preventDefault();
    zoomBy(e.deltaY < 0 ? STEP : -STEP);
  }
  function handleMouseDown(e) {
    if (!isImage || scale <= 1) return;
    setDragging(true);
    dragRef.current = { startX: e.clientX, startY: e.clientY, originX: offset.x, originY: offset.y };
  }
  function handleMouseMove(e) {
    if (!dragRef.current) return;
    setOffset({
      x: dragRef.current.originX + (e.clientX - dragRef.current.startX),
      y: dragRef.current.originY + (e.clientY - dragRef.current.startY),
    });
  }
  function endDrag() { dragRef.current = null; setDragging(false); }

  // Bigger, higher-contrast buttons for the floating zoom bar (replaces the small
  // low-opacity header icons — UAT feedback: zoom worked but wasn't user-friendly).
  const floatBtnStyle = {
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.22)',
    color: '#fff',
    borderRadius: 8,
    height: 40, minWidth: 40,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  };

  const navArrowStyle = {
    background: 'rgba(255,255,255,0.12)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 8,
    color: '#fff',
    width: 44,
    height: 44,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'background 0.15s',
  };

  const disabledArrowStyle = {
    ...navArrowStyle,
    opacity: 0.2,
    cursor: 'default',
    pointerEvents: 'none',
  };

  return (
    /* Overlay — click to close */
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex', flexDirection: 'column',
        animation: 'fadeIn 0.18s ease',
      }}
    >
      {/* Header — stop propagation so clicks on header don't close modal */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: VA_BLACK,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          padding: '0 16px',
          height: 56,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          flexShrink: 0,
        }}
      >
        {/* Left: receipt metadata */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
          <span style={{ fontWeight: '700', fontSize: 14, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>
            {receipt.vendor || '—'}
          </span>
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap' }}>
            {receipt.employeeName}
          </span>
          <StatusBadge status={receipt.displayStatus || receipt.status} />
          <span style={{ fontWeight: '700', fontSize: 13, color: '#d97706', whiteSpace: 'nowrap' }}>
            {fmt(receipt.amount)}
          </span>
          {receipt.date && (
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap' }}>
              {receipt.date}
            </span>
          )}
        </div>

        {/* Center: file name */}
        <div style={{
          fontSize: 11, color: 'rgba(255,255,255,0.35)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          maxWidth: 200, flexShrink: 1,
        }}>
          {receipt.filename || ''}
        </div>

        {/* Right: close button */}
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: 'rgba(255,255,255,0.7)',
            borderRadius: 6,
            width: 32, height: 32,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <X size={16} />
        </button>
      </div>

      {/* Viewer area */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          padding: '16px',
          minHeight: 0,
        }}
      >
        {/* Prev arrow */}
        <button
          onClick={onPrev}
          style={hasPrev ? navArrowStyle : disabledArrowStyle}
          title="Recibo anterior (←)"
        >
          <ChevronLeft size={22} />
        </button>

        {/* Content */}
        <div style={{
          flex: 1,
          maxWidth: '85vw',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 0,
        }}>
          {isImage && receipt.previewUrl ? (
            <div
              onWheel={handleWheel}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={endDrag}
              onMouseLeave={endDrag}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden', maxWidth: '85vw', maxHeight: '82vh',
                cursor: scale > 1 ? (dragging ? 'grabbing' : 'grab') : 'default',
              }}
            >
              <AuthedReceiptPreview
                src={receipt.previewUrl}
                fileType={receipt.fileType}
                alt={receipt.vendor || 'receipt'}
                imgStyle={{
                  maxHeight: '82vh',
                  maxWidth: '85vw',
                  objectFit: 'contain',
                  borderRadius: 8,
                  boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
                  transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                  transition: dragging ? 'none' : 'transform 0.15s ease',
                  userSelect: 'none',
                  willChange: 'transform',
                }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              />
            </div>
          ) : isPdf && receipt.previewUrl ? (
            <AuthedReceiptPreview
              src={receipt.previewUrl}
              fileType={receipt.fileType}
              alt={receipt.vendor || 'receipt'}
              iframeStyle={{
                width: '85vw',
                height: '82vh',
                border: 'none',
                borderRadius: 8,
              }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            />
          ) : (
            /* Fallback: no preview URL or unknown type */
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
              color: 'rgba(255,255,255,0.4)',
            }}>
              <FileText size={64} />
              <div style={{ fontSize: 14 }}>Vista previa no disponible</div>
            </div>
          )}
        </div>

        {/* Next arrow */}
        <button
          onClick={onNext}
          style={hasNext ? navArrowStyle : disabledArrowStyle}
          title="Siguiente recibo (→)"
        >
          <ChevronRight size={22} />
        </button>
      </div>

      {/* Floating zoom bar — images only. Bottom-center for discoverability; big +/−
          buttons and an explicit "Ajustar" (fit-to-screen) so users don't need the
          mouse wheel. Wheel + drag-to-pan still work as a bonus. */}
      {isImage && receipt.previewUrl && (
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)',
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'rgba(20,20,20,0.82)', backdropFilter: 'blur(6px)',
            border: '1px solid rgba(255,255,255,0.18)', borderRadius: 12,
            padding: '6px 8px', boxShadow: '0 6px 24px rgba(0,0,0,0.5)', zIndex: 1001,
          }}
        >
          <button onClick={() => zoomBy(-STEP)} disabled={scale <= MIN_SCALE}
            style={{ ...floatBtnStyle, opacity: scale <= MIN_SCALE ? 0.35 : 1, cursor: scale <= MIN_SCALE ? 'default' : 'pointer' }}
            title="Alejar">
            <ZoomOut size={20} />
          </button>
          <span style={{ fontSize: 14, fontWeight: '600', color: '#fff', minWidth: 54, textAlign: 'center', userSelect: 'none' }}>
            {Math.round(scale * 100)}%
          </span>
          <button onClick={() => zoomBy(STEP)} disabled={scale >= MAX_SCALE}
            style={{ ...floatBtnStyle, opacity: scale >= MAX_SCALE ? 0.35 : 1, cursor: scale >= MAX_SCALE ? 'default' : 'pointer' }}
            title="Acercar">
            <ZoomIn size={20} />
          </button>
          <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.18)', margin: '0 2px' }} />
          <button onClick={resetZoom} disabled={atDefault}
            style={{ ...floatBtnStyle, width: 'auto', minWidth: 0, padding: '0 12px', gap: 6, opacity: atDefault ? 0.35 : 1, cursor: atDefault ? 'default' : 'pointer' }}
            title="Ajustar a pantalla">
            <Maximize2 size={17} />
            <span style={{ fontSize: 13, fontWeight: '600' }}>Ajustar</span>
          </button>
        </div>
      )}
    </div>
  );
}
